import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Truck, LogOut, User, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import useAuthStore from '@/store/authStore';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { authAPI } from '@/services/api';

const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const { isAuthenticated, user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const isLandingPage = location.pathname === '/';

  // Detect scroll for navbar styling
  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      // Appeler l'API de déconnexion backend pour détruire la session
      await authAPI.logout();
    } catch (error) {
      console.log('Erreur lors de la déconnexion backend:', error);
    } finally {
      // Nettoyer le store frontend
      logout();
      toast({
        title: 'Déconnexion réussie',
        description: 'À bientôt !',
      });
      navigate('/');
    }
  };

  const getDashboardLink = () => {
    if (!user) return '/';
    switch (user.role) {
      case 'CLIENT':
        return '/client/dashboard';
      case 'TRANSPORTEUR':
        return '/transporter/dashboard';
      case 'ADMIN':
        return '/admin/dashboard';
      case 'AGENT':
        return '/admin/dashboard';
      default:
        return '/';
    }
  };

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsOpen(false);
    }
  };

  const navLinks = [
    { label: 'Accueil', action: () => scrollToSection('hero') },
    { label: 'Qui sommes-nous', action: () => scrollToSection('who-we-are') },
    { label: 'Comment ça marche', action: () => scrollToSection('how-it-works') },
    { label: 'Témoignages', action: () => scrollToSection('testimonials') },
    { label: 'Contact', action: () => navigate('/contact') },
  ];

  return (
    <nav className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
      isLandingPage
        ? scrolled
          ? 'bg-white/95 backdrop-blur-lg shadow-lg border-b border-gray-200'
          : 'bg-white/80 backdrop-blur-sm border-b border-gray-100'
        : 'bg-card border-b'
    )}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className="relative">
              <img src="/logo.png" alt="LogiConnect" className="h-20 w-auto object-contain group-hover:scale-105 transition-transform" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full animate-pulse" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          {isLandingPage && (
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link, index) => (
                <button
                  key={index}
                  onClick={link.action}
                  className="relative text-gray-700 hover:text-[#0d5850] font-medium transition-colors group py-2"
                >
                  {link.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#0d5850] to-teal-600 transition-all group-hover:w-full" />
                </button>
              ))}
            </div>
          )}

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-3">
            {isAuthenticated ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate(getDashboardLink())}
                  className="text-gray-700 hover:text-[#0d5850] hover:bg-teal-50"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Tableau de Bord
                </Button>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </Button>
              </>
            ) : (
              isLandingPage && <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/auth?tab=login')}
                  className="text-gray-700 hover:text-[#0d5850] hover:bg-teal-50 font-medium"
                >
                  Connexion
                </Button>
                <Button
                  onClick={() => navigate('/auth?tab=register')}
                  className="bg-gradient-to-r from-[#0d5850] to-teal-600 hover:from-[#0a4740] hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all px-6"
                >
                  Commencer gratuitement
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700 hover:text-[#0d5850] p-2 hover:bg-teal-50 rounded-lg transition-colors"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && isLandingPage && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden border-t border-gray-100 overflow-hidden"
            >
              <div className="py-6 space-y-4">
                {navLinks.map((link, index) => (
                  <button
                    key={index}
                    onClick={link.action}
                    className="block w-full text-left px-4 py-3 text-gray-700 hover:text-[#0d5850] hover:bg-teal-50 rounded-lg font-medium transition-colors"
                  >
                    {link.label}
                  </button>
                ))}

                <div className="border-t border-gray-100 pt-4 px-4 space-y-3">
                  {isAuthenticated ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigate(getDashboardLink());
                          setIsOpen(false);
                        }}
                        className="w-full justify-start border-gray-300"
                      >
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Tableau de Bord
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          handleLogout();
                          setIsOpen(false);
                        }}
                        className="w-full justify-start border-gray-300"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Déconnexion
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigate('/auth?tab=login');
                          setIsOpen(false);
                        }}
                        className="w-full justify-start border-gray-300"
                      >
                        Connexion
                      </Button>
                      <Button
                        onClick={() => {
                          navigate('/auth?tab=register');
                          setIsOpen(false);
                        }}
                        className="w-full bg-gradient-to-r from-[#0d5850] to-teal-600 text-white"
                      >
                        Commencer gratuitement
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;
